import type { Renderer as PixiRenderer } from 'pixi.js';

/**
 * Which floating-point render targets this GPU will actually let us draw into.
 *
 * WebGL2 can *sample* half-float textures out of the box, but rendering into one needs an
 * extension, and the two extensions cover different sets: EXT_color_buffer_half_float
 * gives the 16F formats, while EXT_color_buffer_float additionally gives the 32F formats
 * and the packed R11F_G11F_B10F. So half-float support is the union of the two, and it is
 * the only thing this renderer needs — see the note on format choice in PostProcess.
 *
 * Everything here degrades: without these the chain runs exactly as it did before, in
 * 8-bit, and the only visible difference is that highlights clip at white instead of
 * blooming from their real intensity.
 */
export interface FloatTargetSupport {
  /** r16float / rgba16float are colour-renderable. */
  halfFloat: boolean;
}

export function detectFloatTargets(renderer: PixiRenderer): FloatTargetSupport {
  // WebGPU has these formats unconditionally; the probe below is a WebGL question.
  const gl = (renderer as unknown as { gl?: WebGL2RenderingContext }).gl;
  if (!gl || typeof gl.getExtension !== 'function') return { halfFloat: false };

  const extensions = (
    renderer as unknown as { context?: { extensions?: { colorBufferFloat?: unknown } } }
  ).context?.extensions;

  const halfFloat =
    Boolean(extensions?.colorBufferFloat) ||
    Boolean(gl.getExtension('EXT_color_buffer_float')) ||
    Boolean(gl.getExtension('EXT_color_buffer_half_float'));

  return { halfFloat };
}
